import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import type { WalletBalanceSource } from '../lib/walletSource';

type ReceiveAddressModalProps = {
  isOpen: boolean;
  walletAddress: string | null;
  walletSource: WalletBalanceSource;
  onClose: () => void;
};

type CopyStatus = 'idle' | 'success' | 'error';

export default function ReceiveAddressModal({
  isOpen,
  walletAddress,
  walletSource,
  onClose,
}: ReceiveAddressModalProps) {
  const { t } = useTranslation();
  const [copyStatus, setCopyStatus] = useState<CopyStatus>('idle');

  useEffect(() => {
    if (isOpen) setCopyStatus('idle');
  }, [isOpen, walletAddress, walletSource]);

  if (!isOpen) return null;

  const walletSourceLabel = walletSource === 'metamask' ? 'MetaMask' : 'World App';

  const handleCopyAddress = async () => {
    if (!walletAddress) return;

    try {
      if (!navigator.clipboard) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(walletAddress);
      setCopyStatus('success');
    } catch {
      setCopyStatus('error');
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="receive-address-modal" role="dialog" aria-modal="true" aria-labelledby="receive-address-title">
        <div className="modal-header">
          <div>
            <p className="eyebrow">{walletSourceLabel} · WORLD CHAIN</p>
            <h2 id="receive-address-title">{t('receive.title')}</h2>
          </div>
          <button className="modal-close-button" type="button" onClick={onClose} aria-label={t('receive.close')}>
            ×
          </button>
        </div>

        {walletAddress ? (
          <div className="receive-address-content">
            <div className="receive-qr-code">
              <QRCodeSVG
                value={walletAddress}
                size={220}
                level="M"
                marginSize={4}
                bgColor="#f5fff8"
                fgColor="#07130f"
                title={t('receive.qrTitle', { source: walletSourceLabel })}
              />
            </div>
            <p className="receive-source-label">{t('receive.address', { source: walletSourceLabel })}</p>
            <p className="receive-chain-label">{t('receive.chainAddress')}</p>
            <p className="receive-address-value">{walletAddress}</p>
            <button className="auth-button receive-copy-button" type="button" onClick={() => void handleCopyAddress()}>
              {t('receive.copy')}
            </button>
            {copyStatus === 'success' && <p className="receive-copy-status" role="status">{t('receive.copied')}</p>}
            {copyStatus === 'error' && <p className="receive-copy-error" role="alert">{t('receive.copyFailed')}</p>}
          </div>
        ) : (
          <p className="receive-address-empty">
            {t('receive.unavailable', { source: walletSourceLabel })}
          </p>
        )}
      </section>
    </div>
  );
}
